import React from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { BY_ROLE_PAGES, featureManifest } from "../featureManifest";
import { getMock } from "../components/FeatureMockups";
import FinalCTA from "../components/FinalCTA";

export default function RolePageTemplate() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/product\//, "").replace(/\/$/, "");
  const role = BY_ROLE_PAGES.find((r) => r.slug === slug);
  if (!role) return <Navigate to="/product" replace />;
  const keyFeatures = (role.keyFeatures || []).map((s) => featureManifest.find((f) => f.slug === s)).filter(Boolean);

  return (
    <div data-testid={`mk-role-${role.slug}`}>
      {/* HERO */}
      <section className="mk-section !pb-12 relative overflow-hidden">
        <div className="mk-grid-bg absolute inset-0 opacity-50 pointer-events-none" />
        <div className="mk-container relative z-10">
          <div className="grid gap-10 lg:gap-16 items-center lg:grid-cols-2">
            <div>
              <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-4">
                {role.nav}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mk-display leading-[1.05]">
                {role.headline}
              </h1>
              <p className="mt-6 text-base md:text-lg text-[var(--mk-text-dim)] leading-relaxed max-w-xl">
                {role.subheadline}
              </p>
              <div className="mt-8 flex gap-3 flex-wrap">
                <Link to="/signup" className="mk-btn mk-btn-primary text-base px-5 py-3" data-testid={`mk-role-cta-${role.slug}`}>
                  Start free trial →
                </Link>
                <Link to="/product/demo" className="mk-btn mk-btn-secondary text-base px-5 py-3">
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden border" style={{ borderColor: "var(--mk-border-accent)", aspectRatio: "4 / 3" }}>
              <img
                src={role.heroImage}
                alt={role.nav}
                className="w-full h-full object-cover"
                style={{ filter: "saturate(0.9) contrast(1.1)" }}
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,20,20,0.2) 0%, rgba(20,20,20,0.75) 100%)" }} />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="mk-mono text-[9px] uppercase tracking-[0.25em] text-[var(--mk-accent)]">Built for</div>
                <div className="text-xl font-bold mk-display">{role.nav.replace("For ", "")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KEY FEATURES for this role */}
      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-3">
            What you'll live in
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mk-display mb-10">
            The features that matter most to your role.
          </h2>
          <div className="space-y-12 lg:space-y-20">
            {keyFeatures.map((f, i) => {
              const Mock = getMock(f.sections?.[0]?.screenshotSlug || "");
              const reverse = i % 2 === 1;
              return (
                <div key={f.slug} className={`grid gap-10 lg:gap-16 items-center md:grid-cols-2 ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
                  <div>
                    <div className="mk-mono text-[10px] uppercase tracking-[0.25em] text-[var(--mk-text-muted)] mb-3">{f.category}</div>
                    <h3 className="text-2xl md:text-3xl font-bold mk-display mb-3">{f.title}</h3>
                    <p className="text-[var(--mk-text-dim)]">{f.subtitle}</p>
                    <Link to={`/product/${f.slug}`} className="mk-btn mk-btn-secondary text-sm mt-5">
                      Deep dive →
                    </Link>
                  </div>
                  <Mock />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA headline={role.headline} sub={role.subheadline} />
    </div>
  );
}
